import React, { useState, useEffect } from 'react';
import { Mail, Building2, User, Calendar, CheckCircle, Circle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  const [error, setError] = useState(null);

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = filter === 'unread' 
        ? `${API}/contact/submissions?unread_only=true`
        : `${API}/contact/submissions`;
      
      const response = await axios.get(url);
      setSubmissions(response.data);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (submissionId) => {
    try {
      await axios.patch(`${API}/contact/submissions/${submissionId}/mark-read`);
      // Refresh the list
      fetchSubmissions();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8b2e4a] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0f3f5d] mb-2">Contact Submissions</h1>
          <p className="text-gray-600">View and manage contact form submissions</p>
        </div>

        {/* Filter Buttons */}
        <div className="mb-6 flex space-x-4">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-[#8b2e4a] hover:bg-[#6d2339]' : 'border-[#8b2e4a] text-[#8b2e4a] hover:bg-[#8b2e4a] hover:text-white'}
          >
            All Submissions ({submissions.length})
          </Button>
          <Button
            onClick={() => setFilter('unread')}
            variant={filter === 'unread' ? 'default' : 'outline'}
            className={filter === 'unread' ? 'bg-[#508f9d] hover:bg-[#3d7080]' : 'border-[#508f9d] text-[#508f9d] hover:bg-[#508f9d] hover:text-white'}
          >
            Unread Only
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}

        {/* Submissions List */}
        {submissions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-500 text-lg">No submissions found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <Card 
                key={submission.id}
                className={`${!submission.read ? 'border-l-4 border-l-[#c2983e] bg-white' : 'bg-gray-50'} hover:shadow-lg transition-shadow`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {submission.read ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      ) : (
                        <Circle className="w-5 h-5 text-[#c2983e] mt-1" />
                      )}
                      <div>
                        <CardTitle className="text-xl text-[#0f3f5d]">
                          {submission.name}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-1 text-[#508f9d]" />
                            <a href={`mailto:${submission.email}`} className="hover:text-[#8b2e4a]">
                              {submission.email}
                            </a>
                          </div>
                          {submission.company && (
                            <div className="flex items-center">
                              <Building2 className="w-4 h-4 mr-1 text-[#508f9d]" />
                              {submission.company}
                            </div>
                          )}
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1 text-[#508f9d]" />
                            {formatDate(submission.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                    {!submission.read && (
                      <Button
                        size="sm"
                        onClick={() => markAsRead(submission.id)}
                        className="bg-[#508f9d] hover:bg-[#3d7080] text-white"
                      >
                        Mark as Read
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-sm font-semibold text-[#0f3f5d] mb-2">Message:</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{submission.message}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
